/**
 * Per-renderer volume hydration — never show 0 on startup before device confirms.
 * Restore last known volume per zone; default 35% when unknown.
 * Never apply a fetched volume above the stored ceiling for that zone.
 */

export const VOLUME_CACHE_KEY = 'maestro_renderer_volumes';
export const DEFAULT_START_VOLUME = 35;

/** Normalize device-reported volume to 0–100 (handles 0–1 float mistakes). */
export function normalizeVolumePercent(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_START_VOLUME;
  let v = value;
  if (v > 0 && v <= 1) v = Math.round(v * 100);
  return Math.round(Math.max(0, Math.min(100, v)));
}

export function readVolumeCache(rendererId: string): number | null {
  try {
    const raw = localStorage.getItem(VOLUME_CACHE_KEY);
    if (!raw) return null;
    const map = JSON.parse(raw);
    const v = map?.[rendererId];
    if (typeof v !== 'number' || !Number.isFinite(v) || v <= 0) return null;
    return normalizeVolumePercent(v);
  } catch {
    return null;
  }
}

export function writeVolumeCache(rendererId: string, vol: number): void {
  try {
    const raw = localStorage.getItem(VOLUME_CACHE_KEY);
    const map = raw ? JSON.parse(raw) : {};
    map[rendererId] = normalizeVolumePercent(vol);
    localStorage.setItem(VOLUME_CACHE_KEY, JSON.stringify(map));
  } catch { /* quota */ }
}

/** Optimistic slider value before IPC fetch — never 0. */
export function getStartupDisplayVolume(rendererId: string | null | undefined): number {
  if (!rendererId) return DEFAULT_START_VOLUME;
  const cached = readVolumeCache(rendererId);
  if (cached != null) return cached;
  return DEFAULT_START_VOLUME;
}

/**
 * Apply device-reported volume: never above stored ceiling; without stored
 * history never default to device max (use DEFAULT_START_VOLUME).
 */
export function resolveFetchedVolume(rendererId: string, fetched: number): number {
  const stored = readVolumeCache(rendererId);
  if (stored == null) return DEFAULT_START_VOLUME;
  const normalized = normalizeVolumePercent(fetched);
  return Math.min(normalized, stored);
}

/**
 * Device-initiated volume (physical knob / poll push) — always accept the
 * reported level and raise the stored ceiling so the slider can follow upward.
 */
export function resolveInboundDeviceVolume(rendererId: string, fetched: number): number {
  const normalized = normalizeVolumePercent(fetched);
  writeVolumeCache(rendererId, normalized);
  return normalized;
}

/** Value for MiniPlayer slider when store.volume is null. */
export function getDisplayVolume(storeVolume: number | null, rendererId: string | null | undefined): number {
  if (typeof storeVolume === 'number' && Number.isFinite(storeVolume)) {
    return storeVolume;
  }
  return getStartupDisplayVolume(rendererId);
}
